import { Loader2, CheckCircle } from 'lucide-react';
import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Uploader from '~/components/ImageManager/Uploader';
import { useImages } from '~/context/image';
import { useUploadCompletionAck } from '~/hooks/useUploadCompletionAck';

interface ShowUploadProps {
  className?: string;
  onUploadComplete?: (message: { role: string; content: string }) => boolean | void | Promise<boolean | void>;
  uploadAckRetryKey?: string;
}

function ShowUpload({ className = '', onUploadComplete, uploadAckRetryKey }: ShowUploadProps) {
  const { t } = useTranslation('artwork');
  const { uploadingImages, images } = useImages();
  const uploadingImagesList = Object.values(uploadingImages);

  // Track image IDs that were uploaded during this tool session
  const sessionImageIds = useRef<Set<string>>(new Set());
  const hasStartedUploading = useRef<boolean>(false);

  // Track when user starts uploading
  if (uploadingImagesList.length > 0) {
    hasStartedUploading.current = true;
  }

  // Track images that are actively uploading in this session
  useEffect(() => {
    uploadingImagesList.forEach((img) => {
      // If this image has a serverId, it means it completed upload and we should track it
      if (img.serverId) {
        sessionImageIds.current.add(img.serverId);
      }
    });
  }, [uploadingImagesList]);

  // Get session-specific images (uploading + processed from this session only)
  const sessionProcessedImages = images.filter(
    (img) => sessionImageIds.current.has(img.id) && img.status === 'processed'
  );
  const sessionImages = [...uploadingImagesList, ...sessionProcessedImages];

  // Check if upload session is complete
  const isUploadComplete =
    hasStartedUploading.current && uploadingImagesList.length === 0 && sessionProcessedImages.length > 0;
  const showUploader = !hasStartedUploading.current;

  useUploadCompletionAck({
    isUploadComplete,
    messageContent: t('upload.uploaded-message', { count: sessionProcessedImages.length }),
    uploadAckRetryKey,
    onUploadComplete,
  });

  // If upload is complete, show completion message
  if (isUploadComplete) {
    return (
      <div
        className={`flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200 my-4 ${className}`}
      >
        <CheckCircle className="w-4 h-4" />
        <span>{t('upload.uploaded-count', { count: sessionProcessedImages.length })}</span>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden my-4 ${className}`}>
      {/* Show thumbnails of session-specific uploading/processing images */}
      {sessionImages.length > 0 && (
        <div className="text-xs text-gray-400 font-light flex items-center gap-1 mb-2">
          {sessionImages.map((image) => {
            // Handle different image sources based on type
            const imageSource = image.preview || (image as any).thumbnailUrl;
            const imageAlt = image.fileMeta?.fileName || (image as any).title || 'image';

            return (
              <div key={image.id} className="relative w-10 h-10 rounded-md overflow-hidden">
                {imageSource ? (
                  <img src={imageSource} alt={imageAlt} className="w-full h-full object-cover rounded-md" />
                ) : (
                  <div className="w-full h-full bg-gray-200 rounded-md" />
                )}

                {/* Upload progress overlay */}
                {image.status === 'uploading' &&
                  typeof image.uploadProgress === 'number' &&
                  image.uploadProgress < 100 && (
                    <div className="absolute inset-0 bg-black/50 rounded-md flex flex-col items-center justify-center p-1">
                      <div className="w-full bg-gray-700 rounded-full h-0.5 overflow-hidden">
                        <div
                          className="bg-white h-0.5 rounded-full transition-all duration-300 ease-in-out"
                          style={{ width: `${image.uploadProgress || 0}%` }}
                        />
                      </div>
                    </div>
                  )}

                {/* Processing spinner */}
                {['uploaded', 'processing'].includes(image.status || '') && (
                  <div className="absolute inset-0 rounded-md flex items-center justify-center bg-black/20">
                    <Loader2 className="w-3 h-3 text-white animate-spin" />
                  </div>
                )}

                {/* Error state */}
                {image.status === 'error' && (
                  <div className="absolute inset-0 rounded-md flex items-center justify-center bg-red-500/50">
                    <span className="text-white text-xs">!</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showUploader && (
        <div className="max-h-[400px] overflow-hidden">
          <Uploader
            source="arty_upload_card"
            isOpen={true}
            onOpenChange={() => {}}
            isFullPage={true}
            showTitle={false}
            isOnboarding={false}
            isChat={true}
          />
        </div>
      )}
    </div>
  );
}

export default ShowUpload;
